<template>
  <div>
    <alert
      :message="deletePlanningStatus.message"
      :modalActive="modalActive"
      :isError="deletePlanningStatus.isError"
      @close="closeNotification"
    />
    <transition name="fade">
      <div class="modal" v-show="isOpen">
        <transition name="drop-in">
          <div class="modal-inner" v-show="isOpen" ref="target">
            <div class="modal-content">
              <h1 class="title">{{ title }}</h1>

              <div
                v-if="props.event.existProgram === false"
                class="no-program-container"
              >
                <div class="no-program-message">
                  <p class="text-gray-500 text-center py-8">
                    No Program Available
                  </p>
                </div>
              </div>

              <div
                v-if="props.event.existProgram === true"
                class="program-container"
              >
                <!-- Tabs Section -->
                <div class="tabs-section">
                  <div class="tabs-container">
                    <button
                      v-for="(program, index) in props.event.allProgram"
                      :key="index"
                      :class="{ active: selectedProgramId === program.id }"
                      @click="onTabClick(program.id, program.programName)"
                      class="tab-button"
                    >
                      {{ program.programName }}
                    </button>
                  </div>
                </div>

                <!-- Content Section -->
                <div class="content-section">
                  <!-- Date Information -->
                  <div class="date-info-section">
                    <div class="date-grid">
                      <div class="date-item">
                        <p class="date-label">{{ t("startDate") }}</p>
                        <p class="date-value">{{ props.event.start }}</p>
                      </div>
                      <div class="date-item">
                        <p class="date-label">{{ t("endDate") }}</p>
                        <p class="date-value">{{ props.event.end }}</p>
                      </div>
                    </div>
                  </div>

                  <!-- Start Time Section -->
                  <div class="info-section">
                    <h3 class="section-title">{{ t("startTime") }}</h3>

                    <div
                      v-if="planningStore.getPlanningLoading"
                      class="loading-container"
                    >
                      <div class="loading-skeleton"></div>
                    </div>

                    <div v-else class="table-wrapper">
                      <table class="info-table">
                        <thead>
                          <tr>
                            <th>Number</th>
                            <th>{{ t("hour") }}</th>
                            <th>{{ t("minute") }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="(station, index) in planning.startTime"
                            :key="index"
                          >
                            <td>{{ index + 1 }}</td>
                            <td>{{ station.hour }}</td>
                            <td>{{ station.minute }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <!-- Stations Active Section -->
                  <div class="info-section">
                    <h3 class="section-title">Stations Active</h3>

                    <div
                      v-if="planningStore.getPlanningLoading"
                      class="loading-container"
                    >
                      <div class="loading-skeleton"></div>
                    </div>

                    <div
                      v-else-if="planning.isStationEmpty"
                      class="empty-state"
                    >
                      <p class="empty-message">No Station Available</p>
                    </div>

                    <div v-else class="table-wrapper">
                      <table class="info-table">
                        <thead>
                          <tr v-if="planning.mode === 1">
                            <th>{{ t("stations") }}</th>
                            <th>{{ t("minute") }}</th>
                            <th>{{ t("second") }}</th>
                          </tr>
                          <tr v-if="planning.mode === 2">
                            <th>{{ t("stations") }}</th>
                            <th>{{ t("hour") }}</th>
                            <th>{{ t("minute") }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="station in planning.stations?.[0] || []"
                            :key="station.field"
                          >
                            <td>{{ station.groupName }}</td>
                            <td>{{ station.value?.split(".")[0] }}</td>
                            <td>{{ station.value?.split(".")[1] }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </transition>
  </div>
</template>

<script setup>
import BaseButton from "@/components/button/BaseButton.vue";
import { useDeviceManagement } from "@/stores/DeviceManagementStore";
import { storeToRefs } from "pinia";
import { ref, onMounted, onBeforeUpdate, watch } from "vue";
import { onClickOutside } from "@vueuse/core";
import { useI18n } from "vue-i18n";
import { usePlanningStore } from "@/stores/planning/PlanningStore";

//stores
const planningStore = usePlanningStore();
const { deletePlanningStatus, planning, deletePlanningLoading } = storeToRefs(
  usePlanningStore()
);

const { t } = useI18n();

const props = defineProps({
  isOpen: Boolean,
  title: String,
  id: String,
  deviceCode: String,
  event: Object,
});

const selectedProgramId = ref(0);
const programName = ref("");

function onTabClick(id, name) {
  selectedProgramId.value = id;
  planningStore.planning = [];
  planningStore.getPlanning(id, { deviceCode: props.deviceCode });
  programName.value = name;
  console.log(`[ScheduleDetail] Tab clicked: ${name} (ID: ${id})`);
}

watch(
  () => props.isOpen,
  () => {
    if (props.isOpen) {
      console.log("[ScheduleDetail] Modal opened with props:", {
        isOpen: props.isOpen,
        title: props.title,
        id: props.id,
        deviceCode: props.deviceCode,
        event: props.event,
      });

      if (props.event.existProgram && props.event.allProgram?.length > 0) {
        const firstProgram = props.event.allProgram[0];
        selectedProgramId.value = firstProgram.id;
        programName.value = firstProgram.programName;

        planningStore.planning = [];
        planningStore.getPlanning(firstProgram.id, {
          deviceCode: props.deviceCode,
        });

        console.log(
          "[ScheduleDetail] Loaded default first program:",
          firstProgram
        );
      } else {
        console.log("[ScheduleDetail] No program exists for this date");
      }
    }
  }
);

const modalActive = ref(false);
const cancelLabel = ref(t("cancel"));
const deleteLabel = ref(t("delete"));
const regButtonClick = ref(0);
const cancelButtonClick = ref(0);

const deletePlanning = async () => {
  regButtonClick.value = ++regButtonClick.value;
  if (regButtonClick.value == 1) {
    deleteLabel.value = t("dataCorrect");
  }

  if (regButtonClick.value == 2) {
    await planningStore.deletePlanning(props.id);
    emits("close");
    modalActive.value = true;
    setTimeout(closeNotification, 3000);
    deleteLabel.value = t("delete");
    regButtonClick.value = 0;
  }
};

const closeNotification = () => {
  modalActive.value = false;
};

// Define custom events
const emits = defineEmits(["close"]);

const form = ref(null);
const target = ref(null);

onClickOutside(target, () => {
  cancelButtonClick.value = 0;
  regButtonClick.value = 0;
  deleteLabel.value = t("delete");
  cancelLabel.value = t("cancel");
  if (props.isOpen) {
    emits("close");
  }
});

function cancelForm() {
  cancelButtonClick.value = ++cancelButtonClick.value;
  switch (cancelButtonClick.value) {
    case 1:
      cancelLabel.value = t("dataLost");
      break;
    case 2:
      emits("close");
      cancelButtonClick.value = 0;
      cancelLabel.value = t("cancel");
      break;
  }
}
</script>

<style scoped>
/* Modal Layout */
.modal {
  @apply bg-black/20 backdrop-blur-sm w-full h-full fixed top-0 left-0 px-4 py-6 overflow-x-hidden overflow-y-auto z-[999999] flex justify-center items-center;
}

.modal-inner {
  @apply bg-white rounded-2xl border border-gray-200 shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden;
}

.modal-content {
  @apply p-6 sm:p-8 overflow-y-auto max-h-[85vh];
}

/* Title */
.title {
  @apply text-2xl sm:text-3xl font-semibold text-gray-800 mb-6 pb-4 border-b border-gray-200;
}

/* No Program State */
.no-program-container {
  @apply py-8;
}

.no-program-message {
  @apply bg-gray-50 rounded-lg p-6 border border-gray-200;
}

/* Program Container */
.program-container {
  @apply space-y-6;
}

/* Tabs Section */
.tabs-section {
  @apply mb-8;
}

.tabs-container {
  @apply flex gap-2 overflow-x-auto pb-2 border-b border-gray-200;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
}

.tabs-container::-webkit-scrollbar {
  height: 4px;
}

.tabs-container::-webkit-scrollbar-track {
  background: transparent;
}

.tabs-container::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 2px;
}

.tabs-container::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.tab-button {
  @apply px-4 py-2 text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors duration-200 whitespace-nowrap rounded-t-lg border-b-2 border-transparent hover:border-blue-200;
}

.tab-button.active {
  @apply text-blue-600 border-blue-500 bg-blue-50;
}

/* Content Section */
.content-section {
  @apply space-y-6;
}

/* Date Information */
.date-info-section {
  @apply bg-gray-50 rounded-lg p-4;
}

.date-grid {
  @apply grid grid-cols-1 sm:grid-cols-2 gap-4;
}

.date-item {
  @apply space-y-1;
}

.date-label {
  @apply text-sm font-medium text-gray-600;
}

.date-value {
  @apply text-base text-gray-900 font-medium;
}

/* Info Sections */
.info-section {
  @apply bg-white border border-gray-200 rounded-lg p-5;
}

.section-title {
  @apply text-lg font-semibold text-gray-800 mb-4;
}

/* Loading State */
.loading-container {
  @apply py-4;
}

.loading-skeleton {
  @apply h-3 bg-gray-200 rounded-full animate-pulse w-48;
}

/* Empty State */
.empty-state {
  @apply py-6;
}

.empty-message {
  @apply text-gray-500 text-center italic;
}

/* Table Styling */
.table-wrapper {
  @apply overflow-hidden rounded-lg border border-gray-200;
}

.info-table {
  @apply w-full border-collapse;
}

.info-table thead {
  @apply bg-gray-50;
}

.info-table th {
  @apply px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b border-gray-200;
}

.info-table td {
  @apply px-4 py-3 text-sm text-gray-900 border-b border-gray-100;
}

.info-table tbody tr:hover {
  @apply bg-gray-50;
}

.info-table tbody tr:last-child td {
  @apply border-b-0;
}

/* Scrollable tbody for large datasets */
.info-table tbody {
  @apply block max-h-60 overflow-y-auto;
}

.info-table thead,
.info-table tbody tr {
  @apply table table-fixed w-full;
}

.info-table th,
.info-table td {
  @apply w-1/3;
}

/* Animations */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.drop-in-enter-active,
.drop-in-leave-active {
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.drop-in-enter-from,
.drop-in-leave-to {
  opacity: 0;
  transform: translateY(-30px) scale(0.95);
}

/* Responsive Design */
@media (max-width: 640px) {
  .modal {
    @apply px-2 py-4;
  }

  .modal-content {
    @apply p-4;
  }

  .title {
    @apply text-xl mb-4 pb-3;
  }

  .info-table th,
  .info-table td {
    @apply px-2 py-2 text-xs;
  }

  .section-title {
    @apply text-base mb-3;
  }

  .date-grid {
    @apply grid-cols-1 gap-3;
  }
}
</style>
